import { claude } from '../api.js';
import { ST } from '../state.js';
import { COPYWRITING_BRIEF_SCHEMA } from './constants.js';

// Pure prompt construction — no DOM dependency. Direct migration to utility functions.
// ── EXISTING-SITE PROMPT BUILDERS ───────────────────────────────
// Prompt builders for keep/refresh/rebrand flows.
// Dependencies: $() (runtime only)

/**
 * Build XML fact inventory block from marketing brief facts.
 * @param {import('../types.ts').MarketingBriefFacts} facts
 * @returns {string}
 */
function buildFactBlock(facts) {
  if (!facts) {
    return '';
  }
  const lines = ['<fact_inventory>'];
  if (facts.phones?.length) {
    lines.push(`<phones>${facts.phones.join(', ')}</phones>`);
  }
  if (facts.emails?.length) {
    lines.push(`<emails>${facts.emails.join(', ')}</emails>`);
  }
  if (facts.addresses?.length) {
    lines.push(`<addresses>${facts.addresses.join('; ')}</addresses>`);
  }
  if (facts.serviceArea) {
    lines.push(`<service_area>${facts.serviceArea}</service_area>`);
  }
  if (facts.services?.length) {
    lines.push('<services>');
    facts.services.forEach(sv =>
      lines.push(
        `  ${sv.name}${sv.description ? `: ${sv.description}` : ''}${
          sv.price ? ` — ${sv.price}` : ''
        }`,
      ),
    );
    lines.push('</services>');
  }
  if (facts.credentials?.length) {
    lines.push(`<credentials>${facts.credentials.join(' | ')}</credentials>`);
  }
  if (facts.namedStaff?.length) {
    lines.push(`<staff>${facts.namedStaff.map(p => `${p.name}: ${p.role}`).join(', ')}</staff>`);
  }
  if (facts.guarantees?.length) {
    lines.push(`<guarantees>${facts.guarantees.join(', ')}</guarantees>`);
  }
  if (facts.yearsInBusiness) {
    lines.push(`<years>${facts.yearsInBusiness}</years>`);
  }
  if (facts.testimonials?.length) {
    lines.push('<testimonials>');
    facts.testimonials.forEach(t => lines.push(`  "${t.quote}" — ${t.author}`));
    lines.push('</testimonials>');
  }
  if (facts.stats?.length) {
    lines.push(`<stats>${facts.stats.map(st => `${st.number}: ${st.label}`).join(', ')}</stats>`);
  }
  lines.push('</fact_inventory>');
  return lines.length > 2 ? `\n${lines.join('\n')}` : '';
}

/**
 * Option 1: Keep -- preserve all content, redesign visually.
 * @param {object} scrape - Scraped site data
 * @returns {string}
 */
function buildKeepNote(scrape) {
  const dir = $('keep-direction')?.value?.trim() || '';
  const ins = $('keep-inspo')?.value?.trim() || '';

  const lines = [`<content_strategy mode="keep" url="${ST.existingUrl}">`];

  lines.push(`<keep_rules>
- Preserve all original copy verbatim in copyNotes — every headline, paragraph, CTA, testimonial, and stat.
- heroHeadline and ctaText should come from the original site content.
- Your job is the visual design only: create a stunning designVision, derive tokens from it, and write visualBriefs that transform the site's appearance.
- Keep the exact section plan from the original structure — same section types, same order, same count. Do not add, remove, or change section types.
- Override self_check items that conflict: section count matches the original (ignore 7-10 rule), heroHeadline comes from the original (ignore 6-10 words rule).
</keep_rules>`);

  const ss = ST.marketingBrief?.siteStructure || [];
  if (ss.length) {
    lines.push('<original_structure>');
    ss.forEach(s => {
      lines.push(`  ${s.order}. [${s.type}] ${s.label}`);
      if (s.content) {
        lines.push(`     Content: ${s.content}`);
      }
    });
    lines.push('</original_structure>');
  }

  // factBlock NOT included here — already in <marketing_brief> via buildMarketingBriefContext()
  // Including it twice wastes tokens and confuses the model with duplicate data.

  const userDir = [];
  if (dir) {
    userDir.push(`<visual_direction>${dir}</visual_direction>`);
  }
  if (ins) {
    userDir.push(`<inspiration>${ins}</inspiration>`);
  }
  if (userDir.length) {
    lines.push(`<user_direction>\n${userDir.join('\n')}\n</user_direction>`);
  }

  lines.push(noHallucinationRules());
  lines.push('</content_strategy>');
  return lines.join('\n');
}

/**
 * Option 2: Refresh -- rewrite copy for conversion.
 * @param {object} scrape - Scraped site data
 * @param {object} brief - Copywriting brief
 * @returns {string}
 */
function buildRefreshNote(scrape, brief) {
  const aud = $('refresh-audience')?.value?.trim() || '';
  const ton = $('refresh-tone')?.value?.trim() || '';
  const gap = $('refresh-gaps')?.value?.trim() || '';
  const dir = $('refresh-direction')?.value?.trim() || '';
  const ins = $('refresh-inspo')?.value?.trim() || '';

  const lines = [`<content_strategy mode="refresh" url="${ST.existingUrl}">`];

  if (brief) {
    lines.push('<copywriting_brief>');
    lines.push(`<positioning>${brief.positioning || ''}</positioning>`);
    lines.push(`<audience>${brief.audiencePrimary || ''}</audience>`);
    lines.push(`<voice>${brief.voiceSummary || ''}</voice>`);
    if (brief.currentWeaknesses?.length) {
      lines.push(`<weaknesses>${brief.currentWeaknesses.join('; ')}</weaknesses>`);
    }
    lines.push(`<recommended_headline>${brief.recommendedHeadline || ''}</recommended_headline>`);
    lines.push(`<recommended_cta>${brief.recommendedCTA || ''}</recommended_cta>`);
    if (brief.keyMessages?.length) {
      lines.push(`<key_messages>${brief.keyMessages.join('; ')}</key_messages>`);
    }
    if (brief.sectionOrder?.length) {
      lines.push(`<section_order>${brief.sectionOrder.join(', ')}</section_order>`);
    }
    lines.push('</copywriting_brief>');
  }

  const ss = ST.marketingBrief?.siteStructure || [];
  if (ss.length) {
    lines.push('<original_structure>');
    ss.forEach(s => lines.push(`  ${s.order}. [${s.type}] ${s.label}`));
    lines.push('</original_structure>');
  }

  // factBlock NOT included here — already in <marketing_brief> via buildMarketingBriefContext()

  const userDir = [];
  if (aud) {
    userDir.push(`<target_customer>${aud}</target_customer>`);
  }
  if (ton) {
    userDir.push(`<desired_tone>${ton}</desired_tone>`);
  }
  if (gap) {
    userDir.push(`<current_gap>${gap}</current_gap>`);
  }
  if (dir) {
    userDir.push(`<visual_direction>${dir}</visual_direction>`);
  }
  if (ins) {
    userDir.push(`<inspiration>${ins}</inspiration>`);
  }
  if (userDir.length) {
    lines.push(`<user_direction>\n${userDir.join('\n')}\n</user_direction>`);
  }

  lines.push(noHallucinationRules());
  lines.push('</content_strategy>');
  return lines.join('\n');
}

/**
 * Option 3: Rebrand -- everything new, facts as constraints only.
 * @param {object} scrape - Scraped site data
 * @returns {string}
 */
function buildRebrandNote(scrape) {
  const dir = $('rebrand-direction')?.value?.trim() || '';
  const aud = $('rebrand-audience')?.value?.trim() || '';
  const ins = $('rebrand-inspo')?.value?.trim() || '';

  const lines = [`<content_strategy mode="rebrand" url="${ST.existingUrl}">`];

  lines.push(`<owner_direction>${dir}</owner_direction>`);

  lines.push(`<rebrand_rules>
- Create fresh positioning, messaging, visual identity, and section plan from scratch — design the new brand identity fresh from the owner's direction.
- The new brand should feel like it was born this way.
- Prefer stock photos and CSS visuals over old site images.
</rebrand_rules>`);

  // factBlock NOT included here — already in <marketing_brief> via buildMarketingBriefContext()

  lines.push(`<creative_direction>
- Reason about who the new target customer is and what voice resonates with them
- Pick fonts, colors, and design vision that match the new direction, not the old brand
- Optimize section plan for the new target audience and desired action
- brandVoiceSample should demonstrate the new voice
</creative_direction>`);

  const userDir = [];
  if (aud) {
    userDir.push(`<new_audience>${aud}</new_audience>`);
  }
  if (ins) {
    userDir.push(`<admired_brand>${ins}</admired_brand>`);
  }
  if (userDir.length) {
    lines.push(`<user_direction>\n${userDir.join('\n')}\n</user_direction>`);
  }

  lines.push(noHallucinationRules());
  lines.push('</content_strategy>');
  return lines.join('\n');
}

function noHallucinationRules() {
  return `<fact_accuracy>
- Use only verified facts from the inventory to maintain trust — years, prices, guarantees, certifications, and statistics should come from the fact inventory.
- General service descriptions, benefit statements, and persuasive copy are expected and encouraged.
- Use "[X]" placeholders only when a section specifically needs a numeric fact missing from the fact inventory.
- Write confident, complete copy for every section.
</fact_accuracy>`;
}

/**
 * Copywriting Brief generator (Option 2 only).
 * @param {object} scrape - Scraped site data
 * @param {object} facts - Extracted facts from marketing brief
 * @returns {Promise<object>}
 */
async function generateCopywritingBrief(scrape, facts) {
  const sys = `<role>
You are a conversion copywriter reviewing an existing website for a content refresh.
</role>

<task>
Analyze the current copy weaknesses and produce a strategic brief that will guide the rewrite.
</task>

<rules>
- Preserve every fact from the Fact Inventory in its original form in the refreshed copy
- Use only facts, services, and credentials present in the source material
- Write in the voice of the business, not generic marketing language
- Identify specific weaknesses: vague headlines, buried proof, weak CTAs, missing urgency
- Recommend a section order optimized for conversion
</rules>

<self_check>
Before outputting, verify: (1) every recommendation references a specific weakness from the current content, (2) recommended headline and CTA are specific to this business, (3) section order follows conversion logic.
</self_check>`;

  const mb = ST.marketingBrief;
  const ss = mb?.siteStructure || [];
  const structSummary = ss.length
    ? ss.map(s => `[${s.type}] ${s.label}: ${(s.content || '').slice(0, 200)}`).join('\n')
    : mb?.tagline || '';
  const user = `<fact_inventory>\n${JSON.stringify(facts)}\n</fact_inventory>

<current_content>
${structSummary}
</current_content>`;

  const result = await claude({
    sys,
    user,
    model: 'claude-sonnet-4-6',
    tokens: 2000,
    effort: 'high',
    thinking: { type: 'adaptive' },
    schema: COPYWRITING_BRIEF_SCHEMA,
  });

  return JSON.parse(result);
}

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export {
  buildKeepNote,
  buildRebrandNote,
  buildRefreshNote,
  generateCopywritingBrief,
  noHallucinationRules,
  buildFactBlock,
};
